import { LessonSlide } from '@/hooks/useQueryHooks';
import LearnSlide from './LearnSlide';
import FAQSlide from './FAQSlide';
import MCQSlide from './MCQSlide';
import SpotTheMistakeSlide from './SpotTheMistakeSlide';
import FillInBlankSlide from './FillInBlankSlide';
import TrueFalseSlide from './TrueFalseSlide';
import MatchingSlide from './MatchingSlide';
import NextStepsSlide from './NextStepsSlide';
import QuizSlide from './QuizSlide';
import FlashcardSlide from './FlashcardSlide';

interface ActivitySlideViewProps {
  slide: LessonSlide;
  slideNumber: number;
  totalSlides: number;
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  onMarkComplete?: (slideId: string) => void;
}

const ActivitySlideView = ({ 
  slide,
  slideNumber,
  totalSlides,
  lessonTitle,
  lessonThumbnail,
  onNext, 
  onBack,
  onBackToLesson,
  onMarkComplete
}: ActivitySlideViewProps) => {
  const baseProps = {
    slideNumber,
    totalSlides,
    title: slide.title,
    subtitle: slide.slide_type,
    lessonTitle,
    lessonThumbnail,
    onNext,
    onBack,
    onBackToLesson,
    isCompleted: slide.completed,
    onMarkComplete: onMarkComplete ? () => onMarkComplete(slide.id) : undefined,
  };

  // Route to appropriate slide component based on slide_type
  
  // Learn slides (intro, learn, learn_hands_on)
  if (slide.slide_type === 'intro' || slide.slide_type === 'learn' || slide.slide_type === 'learn_hands_on') {
    return <LearnSlide {...baseProps} content={{ markdown: (slide.content as any)?.body_md || '', imageUrl: undefined }} />;
  }
  
  // Practice slides
  if (slide.slide_type === 'practice_faq') {
    return <FAQSlide {...baseProps} content={{ questions: (slide.structured as any)?.items || [] }} />;
  }
  
  if (slide.slide_type === 'practice_spot') {
    return <SpotTheMistakeSlide {...baseProps} content={{ items: (slide.structured as any)?.items || [] }} />;
  }
  
  if (slide.slide_type === 'practice_match') {
    return <MatchingSlide {...baseProps} content={{ pairs: (slide.structured as any)?.items || [] }} />;
  }
  
  // Assessment slides
  if (slide.slide_type === 'assessment_mcq') {
    return <MCQSlide {...baseProps} content={{ items: (slide.structured as any)?.items || [] }} />;
  }
  
  if (slide.slide_type === 'assessment_fill') {
    return <FillInBlankSlide {...baseProps} content={{ items: (slide.structured as any)?.items || [] }} />;
  }
  
  if (slide.slide_type === 'assessment_true_false') {
    return <TrueFalseSlide {...baseProps} content={{ items: (slide.structured as any)?.items || [] }} />;
  }
  
  // Next steps slide
  if (slide.slide_type === 'next_steps') {
    const nextStepsContent = (slide.structured as any) || {};
    return <NextStepsSlide {...baseProps} content={nextStepsContent} />;
  }

  // Default to learn slide for unknown types
  return <LearnSlide {...baseProps} content={{ markdown: (slide.content as any)?.body_md || 'Content not available', imageUrl: undefined }} />;
};

export default ActivitySlideView;
